// storageKey is a constructor param (not a hardcoded constant) so the portal
// and a foreign page never collide in a shared browser profile.

import { KbChatMessage } from '../api/types';

export interface SavedConversation {
  sessionId: string;
  messages: KbChatMessage[];
  savedAt: number; // epoch ms, used to drop conversations past the backend TTL
}

/**
 * Which Web Storage area backs the saved conversation — i.e. how long it lives.
 *
 * 'local'   → localStorage: outlives the tab, shared across tabs. The portal
 *             wants this; a signed-in user expects their thread to still be
 *             there tomorrow.
 * 'session' → sessionStorage: scoped to the one tab and wiped by the browser
 *             when it closes, so it survives reloads and navigation within the
 *             host site but never a return visit. The public site wants this.
 */
export type StorageKind = 'local' | 'session';

// Mirrors the KB backend's SESSION_TTL_SECONDS (86400 = 24h). Past this the
// backend session is gone, so we don't offer to continue a dead conversation.
const SESSION_TTL_MS = 86_400 * 1000;

/** Resolved lazily at each call site so the existing try/catch also covers a
 * property access that throws outright (some blocked-cookie configurations). */
function storageFor(kind: StorageKind): Storage {
  return kind === 'session' ? window.sessionStorage : window.localStorage;
}

/**
 * Holds the most recent conversation for one widget instance across
 * open/close and page reloads, mirrored to `kind` storage under `storageKey`.
 */
export class SessionStore {
  private saved: SavedConversation | null;

  constructor(
    private storageKey: string,
    private kind: StorageKind = 'local',
  ) {
    this.saved = this.readFromStorage();
  }

  getPrevious(): SavedConversation | null {
    return this.saved;
  }

  save(sessionId: string, messages: KbChatMessage[]): void {
    this.saved = { sessionId, messages: [...messages], savedAt: Date.now() };
    this.writeToStorage(this.saved);
  }

  clear(): void {
    this.saved = null;
    try {
      storageFor(this.kind).removeItem(this.storageKey);
    } catch {
      // Ignore storage errors (private mode / quota); in-memory state is cleared.
    }
  }

  private writeToStorage(conversation: SavedConversation): void {
    try {
      storageFor(this.kind).setItem(this.storageKey, JSON.stringify(conversation));
    } catch {
      // Ignore storage errors; the in-memory copy still works this session.
    }
  }

  private readFromStorage(): SavedConversation | null {
    try {
      const raw = storageFor(this.kind).getItem(this.storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as SavedConversation;
      if (!parsed?.sessionId || !Array.isArray(parsed.messages)) {
        return null;
      }
      // The backend session is gone past its TTL, so don't offer a dead one.
      // Still worth checking under 'session' storage: a tab restored by the
      // browser (reopen-closed-tab, crash recovery) gets its sessionStorage
      // back, however long ago it was closed.
      if (!parsed.savedAt || Date.now() - parsed.savedAt > SESSION_TTL_MS) {
        storageFor(this.kind).removeItem(this.storageKey);
        return null;
      }
      // JSON has no Date type — revive timestamps so timestamp formatting works.
      parsed.messages = parsed.messages.map(m => ({ ...m, timestamp: new Date(m.timestamp) }));
      return parsed;
    } catch {
      return null;
    }
  }
}

/**
 * Static, no-instance-required clear — used by window.AskJumino.clearSession()
 * so a host (e.g. the portal's logout hook) can drop the browser copy without
 * having a live widget instance around.
 */
export function clearStoredSession(storageKey: string, kind: StorageKind = 'local'): void {
  try {
    storageFor(kind).removeItem(storageKey);
  } catch {
    // private mode / quota — nothing to clean up
  }
}
